import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type UnitType =
  | 'unidade' | 'metro' | 'kg' | 'litro'
  | 'caixa'   | 'rolo'  | 'par' | 'pacote' | 'outro';

export type Recurrence =
  | 'semanal' | 'quinzenal' | 'mensal'
  | 'trimestral' | 'semestral' | 'anual' | 'sob_demanda';

export const UNIT_LABELS: Record<UnitType, string> = {
  unidade: 'Unidade (un)',
  metro:   'Metro (m)',
  kg:      'Quilograma (kg)',
  litro:   'Litro (L)',
  caixa:   'Caixa (cx)',
  rolo:    'Rolo',
  par:     'Par',
  pacote:  'Pacote',
  outro:   'Outro',
};

export const RECURRENCE_LABELS: Record<Recurrence, string> = {
  semanal:     'Semanal',
  quinzenal:   'Quinzenal',
  mensal:      'Mensal',
  trimestral:  'Trimestral',
  semestral:   'Semestral',
  anual:       'Anual',
  sob_demanda: 'Sob demanda',
};

export interface SupplierProduct {
  id: string;
  supplier_id: string;
  tenant_id: string;
  product_name: string;
  unit_type: UnitType;
  unit_price_cents: number;
  units_per_purchase: number;
  recurrence: Recurrence;
  last_purchase_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupplierMeta {
  email?: string;
  phone?: string;
  website?: string;
  notes?: string;
}

export interface Supplier {
  id: string;
  name: string;
  document: string | null;
  kind: 'vendor' | 'both';
  meta: SupplierMeta;
  products: SupplierProduct[];
  created_at: string;
}

export interface SupplierProductInput {
  id?: string;
  product_name: string;
  unit_type: UnitType;
  unit_price_cents: number;
  units_per_purchase: number;
  recurrence: Recurrence;
  last_purchase_date: string;
  notes: string;
}

export interface SupplierInput {
  name: string;
  document: string;
  email: string;
  phone: string;
  website: string;
  notes: string;
  products: SupplierProductInput[];
}

// ─── Tradutor ─────────────────────────────────────────────────────────────────

function translateSupplierError(msg: string): string {
  if (msg.includes('row-level security') || msg.includes('RLS') || msg.includes('new row violates'))
    return 'Permissão insuficiente. Verifique se você está autenticado.';
  if (msg.includes('duplicate') || msg.includes('unique'))
    return 'Este fornecedor já está cadastrado com esse nome.';
  if (msg.includes('foreign key') || msg.includes('violates foreign key'))
    return 'Referência inválida. Recarregue a página e tente novamente.';
  if (msg.includes('not-null') || msg.includes('null value'))
    return 'Preencha todos os campos obrigatórios.';
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError'))
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  return 'Ocorreu um erro inesperado. Tente novamente.';
}

// ─── Hook principal ───────────────────────────────────────────────────────────

export function useSuppliers() {
  const { tenantId }          = useTenant();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading]     = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setFetchError(null);

    // Carrega payees do tipo fornecedor + seus produtos
    const { data: payeesData, error: payeesErr } = await supabase
      .from('payees')
      .select('id, name, document, kind, metadata, created_at')
      .eq('tenant_id', tenantId)
      .in('kind', ['vendor', 'both'])
      .order('name');

    if (payeesErr) {
      setFetchError('Erro ao carregar fornecedores.');
      setLoading(false);
      return;
    }

    const payeeIds = ((payeesData ?? []) as any[]).map((p) => p.id);

    let products: SupplierProduct[] = [];
    if (payeeIds.length > 0) {
      const { data: prodData, error: prodErr } = await supabase
        .from('supplier_products')
        .select('*')
        .eq('tenant_id', tenantId)
        .in('supplier_id', payeeIds)
        .order('product_name');

      if (prodErr) {
        setFetchError('Erro ao carregar produtos dos fornecedores.');
        setLoading(false);
        return;
      }
      products = (prodData ?? []) as unknown as SupplierProduct[];
    }

    const result: Supplier[] = ((payeesData ?? []) as any[]).map((p) => ({
      id:         p.id,
      name:       p.name,
      document:   p.document ?? null,
      kind:       p.kind as 'vendor' | 'both',
      meta:       (p.metadata ?? {}) as SupplierMeta,
      products:   products.filter((pr) => pr.supplier_id === p.id),
      created_at: p.created_at,
    }));

    setSuppliers(result);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  // ─── Create ────────────────────────────────────────────────────────────────

  async function addSupplier(input: SupplierInput): Promise<void> {
    if (!tenantId) throw new Error('Tenant não encontrado.');

    const meta: SupplierMeta = {
      email:   input.email.trim()   || undefined,
      phone:   input.phone.trim()   || undefined,
      website: input.website.trim() || undefined,
      notes:   input.notes.trim()   || undefined,
    };

    const { data: payee, error: payeeErr } = await supabase
      .from('payees')
      .insert({
        tenant_id: tenantId,
        name:      input.name.trim(),
        document:  input.document.trim() || null,
        kind:      'vendor',
        metadata:  meta,
      } as never)
      .select('id')
      .single();

    if (payeeErr) throw new Error(translateSupplierError(payeeErr.message));

    const supplierId = (payee as any).id as string;

    if (input.products.length > 0) {
      const rows = input.products
        .filter((p) => p.product_name.trim())
        .map((p) => ({
          supplier_id:        supplierId,
          tenant_id:          tenantId,
          product_name:       p.product_name.trim(),
          unit_type:          p.unit_type,
          unit_price_cents:   p.unit_price_cents,
          units_per_purchase: p.units_per_purchase,
          recurrence:         p.recurrence,
          last_purchase_date: p.last_purchase_date || null,
          notes:              p.notes.trim() || null,
        }));
      if (rows.length > 0) {
        const { error: prodErr } = await supabase.from('supplier_products').insert(rows as never);
        if (prodErr) throw new Error(translateSupplierError(prodErr.message));
      }
    }

    await load();
  }

  // ─── Update ────────────────────────────────────────────────────────────────

  async function updateSupplier(id: string, input: SupplierInput): Promise<void> {
    if (!tenantId) throw new Error('Tenant não encontrado.');

    const meta: SupplierMeta = {
      email:   input.email.trim()   || undefined,
      phone:   input.phone.trim()   || undefined,
      website: input.website.trim() || undefined,
      notes:   input.notes.trim()   || undefined,
    };

    const { error: payeeErr } = await supabase
      .from('payees')
      .update({
        name:     input.name.trim(),
        document: input.document.trim() || null,
        metadata: meta,
      } as never)
      .eq('id', id)
      .eq('tenant_id', tenantId);

    if (payeeErr) throw new Error(translateSupplierError(payeeErr.message));

    // Produtos: deleta os existentes e reinserid (mais simples que upsert individual)
    const { error: delErr } = await supabase
      .from('supplier_products')
      .delete()
      .eq('supplier_id', id)
      .eq('tenant_id', tenantId);

    if (delErr) throw new Error(translateSupplierError(delErr.message));

    const rows = input.products
      .filter((p) => p.product_name.trim())
      .map((p) => ({
        supplier_id:        id,
        tenant_id:          tenantId,
        product_name:       p.product_name.trim(),
        unit_type:          p.unit_type,
        unit_price_cents:   p.unit_price_cents,
        units_per_purchase: p.units_per_purchase,
        recurrence:         p.recurrence,
        last_purchase_date: p.last_purchase_date || null,
        notes:              p.notes.trim() || null,
      }));

    if (rows.length > 0) {
      const { error: prodErr } = await supabase.from('supplier_products').insert(rows as never);
      if (prodErr) throw new Error(translateSupplierError(prodErr.message));
    }

    await load();
  }

  // ─── Delete ────────────────────────────────────────────────────────────────

  async function deleteSupplier(id: string): Promise<void> {
    if (!tenantId) throw new Error('Tenant não encontrado.');
    const { error } = await supabase
      .from('payees')
      .delete()
      .eq('id', id)
      .eq('tenant_id', tenantId);
    if (error) throw new Error(translateSupplierError(error.message));
    await load();
  }

  return { suppliers, loading, fetchError, reload: load, addSupplier, updateSupplier, deleteSupplier };
}
